import React, { useEffect, useRef, useState } from 'react';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Grid, TextField } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import { RootState, useAppDispatch, useAppSelector } from 'redux/store';
import { setImageJson, setRenderRequested, setLastTimeImageJsonDirty } from 'redux/create-style/create-style.slice';
import { v4 as uuidv4 } from 'uuid';
import colorPicker from '../../../assets/img/color_picker.png';
import { ChromePicker } from 'react-color';
import themes from 'styles/themes';

interface ITextEditorProps {
    handleCloseModal: () => void;
    canvasObj?: any;
    value?: string;
    currentEditingTextId?: string;
}

const defaultColors = [
    { color: '#000000' },
    { color: '#00A056' },
    { color: '#02A6E3' },
    { color: '#EF6A31' },
    { color: '#F9BF30' },
    { color: '#FFFFFF' },
];

const defaultFonts = [
    { family: 'Fraunces' },
    { family: 'Poppins' },
    { family: 'Arial' },
    { family: 'ShadowsIntoLightTwo' },
];

export const useStyles = (props) =>
    makeStyles(() => {
        return createStyles({
            wrapper: {
                position: 'relative',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            },
            formContainer: {
                display: 'flex',
                flex: 1,
                justifyContent: 'center',
                alignItems: 'center',
            },
            textStyleContainer: {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                alignSelf: 'center',
                padding: '16px',
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                marginBottom: '20px',
            },
            bottomContainer: {
                height: '70px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: themes.palette.primary.main,
            },
            colorBadgeContainer: {
                display: 'flex',
                flexDirection: 'row',
            },
            colorPicker: {
                boxShadow: 'none !important',
            },
            '@global': {
                '.chrome-picker .flexbox-fix:last-child': {
                    display: 'none !important',
                },
            },
            colorBadge: {
                height: '16px',
                width: '16px',
                borderRadius: '8px',
                border: '1px solid #E7E7E7',
            },
            fontBadge: {
                fontSize: '16px',
                fontWeight: 'bold',
                height: '36px',
                width: '36px',
                borderRadius: '18px',
            },
            colorPickerContainer: {
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'center',
                marginBottom: '20px',
                backgroundColor: 'white',
                padding: '15px',
                borderRadius: '15px',
                width: 'fit-content',
            },
            doneButton: {
                fontSize: '14px',
                color: '#ffffff',
                padding: '8px 16px 8px 16px',
                background: '#1D474F',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '20px',
            },
            cancelButton: {
                fontSize: '14px',
                color: '#1D474F',
                padding: '8px 16px 8px 16px',
                background: '#ffffff',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '20px',
            },
            input: {
                '& .MuiInput-root': {
                    color: props.color || '#ffffff',
                    fontFamily: props.font || 'inherit',
                },
                '& .MuiInputBase-inputMultiline': {
                    // maxHeight: '150px'
                    // height: '100% !important'
                    textAlign: 'center',
                    letterSpacing: '0px',
                },
                '& .MuiInput-underline': {
                    '&.Mui-focused': {
                        borderBottomWidth: 'none',
                        borderBottom: 'none',
                        bottom: '0px',
                    },
                    '&:before': {
                        borderBottom: 'none',
                    },
                    '&:hover:not(.Mui-disabled):before': {
                        borderBottom: 'none',
                    },
                    '&:after': {
                        borderBottom: 'none',
                    },
                },
            },
        });
    });

const TextEditor = ({ handleCloseModal, currentEditingTextId, canvasObj }: ITextEditorProps) => {
    const [selectedFontFamily, setSelectedFontFamily] = useState(defaultFonts[0].family);
    const [selectedFontColor, setSelectedFontColor] = useState(defaultColors[0].color);
    const [tempFontColor, setTempFontColor] = useState(defaultColors[0].color);
    const [isDisplayColorPicker, setIsDisplayColorPicker] = useState(false);
    const [caption, setCaption] = useState('');
    const classes = useStyles({ color: selectedFontColor, font: selectedFontFamily })();
    const dispatch = useAppDispatch();
    const textInputRef = useRef<HTMLInputElement>(null);

    const { imageJson } = useAppSelector((state: RootState) => state.createStyle);

    useEffect(() => {
        if (currentEditingTextId && imageJson.length) {
            let currentEditingTextObj = imageJson.find((item) => item.textId === currentEditingTextId);
            if (currentEditingTextObj) {
                setSelectedFontFamily(currentEditingTextObj.fontFamily);
                setSelectedFontColor(currentEditingTextObj.fontColor);
                setTempFontColor(currentEditingTextObj.fontColor);
                setCaption(currentEditingTextObj.text);
            }
        }
    }, [currentEditingTextId, imageJson]);

    const onSubmit = async () => {
        let udpatedImageJson = imageJson;
        if (currentEditingTextId) {
            udpatedImageJson = udpatedImageJson.map((item) => {
                if (item.textId === currentEditingTextId) {
                    return { ...item, text: caption, fontColor: selectedFontColor, fontFamily: selectedFontFamily };
                } else {
                    return item;
                }
            });
        } else {
            udpatedImageJson = [
                ...udpatedImageJson,
                {
                    top: 100,
                    left: 50,
                    angle: 0,
                    scaleX: 1,
                    scaleY: 1,
                    type: 'text',
                    text: caption,
                    textId: uuidv4(),
                    fontColor: selectedFontColor,
                    fontFamily: selectedFontFamily,
                },
            ];
        }
        dispatch(setImageJson(udpatedImageJson));
        handleCloseModal();
        dispatch(setRenderRequested(true));
        dispatch(setLastTimeImageJsonDirty(new Date().valueOf()));
    };

    const handleColorChange = (color) => {
        setSelectedFontColor(color.hex);
    };

    return (
        <div className={classes.wrapper}>
            <Grid
                container
                style={{
                    padding: '10px',
                    position: 'relative',
                    backgroundColor: '#ffffff',
                    justifyContent: 'flex-end',
                }}
            >
                <CloseIcon style={{ color: themes.palette.primary.main }} onClick={() => handleCloseModal()} />
            </Grid>
            <div
                className={classes.formContainer}
                onClick={() => {
                    if (textInputRef.current) {
                        textInputRef?.current?.focus();
                    }
                }}
            >
                <TextField
                    name="caption"
                    value={caption}
                    className={classes.input}
                    inputRef={textInputRef}
                    autoFocus
                    multiline
                    maxRows={4}
                    onChange={(e) => setCaption(e.target.value)}
                />
            </div>
            {isDisplayColorPicker ? (
                <div className={classes.colorPickerContainer}>
                    <ChromePicker
                        disableAlpha
                        onChangeComplete={handleColorChange}
                        color={selectedFontColor}
                        className={classes.colorPicker}
                    />
                    <div className={`${classes.colorBadgeContainer} justify-content-between`}>
                        <div
                            className={classes.cancelButton}
                            onClick={() => {
                                setIsDisplayColorPicker(false);
                                setSelectedFontColor(tempFontColor);
                            }}
                        >
                            Cancel
                        </div>
                        <div
                            className={classes.doneButton}
                            onClick={() => {
                                setIsDisplayColorPicker(false);
                                setTempFontColor(selectedFontColor);
                            }}
                        >
                            <span>Select</span>
                        </div>
                    </div>
                </div>
            ) : (
                <div className={classes.textStyleContainer}>
                    <div className={`${classes.colorBadgeContainer} mb-3`}>
                        {defaultColors.map((item, index, list) => (
                            <div
                                key={index}
                                className={classes.colorBadge}
                                onClick={() => {
                                    setSelectedFontColor(item.color);
                                    setTempFontColor(item.color);
                                }}
                                style={{
                                    background: item.color,
                                    marginRight: '12px',
                                }}
                            ></div>
                        ))}
                        <img
                            src={colorPicker}
                            className={classes.colorBadge}
                            alt="cp"
                            onClick={() => setIsDisplayColorPicker(true)}
                        />
                    </div>
                    <div className={classes.colorBadgeContainer}>
                        {defaultFonts.map((item, index, list) => (
                            <div
                                key={index}
                                className={classes.fontBadge}
                                onClick={() => setSelectedFontFamily(item.family)}
                                style={{
                                    fontFamily: item.family,
                                    marginRight: list[index + 1] ? '12px' : '0px',
                                    backgroundColor: selectedFontFamily === item.family ? '#000000' : '#ffffff',
                                    color: selectedFontFamily === item.family ? '#ffffff' : '#000000',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    display: 'flex',
                                }}
                            >
                                <span>Aa</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <div className={classes.bottomContainer}>
                <div onClick={() => onSubmit()}>
                    <span
                        style={{
                            marginRight: '10px',
                            fontFamily: 'PoppinsRegular',
                            fontWeight: 400,
                            cursor: 'pointer',
                            color: '#fff',
                        }}
                    >
                        Next
                    </span>
                    <ArrowRightAltIcon style={{ color: 'white' }} />
                </div>
            </div>
        </div>
    );
};

export default TextEditor;
